<template>
  <Teleport to="body">
    <div class="sxm-overlay" @click.self="onClose">
      <div class="sxm-modal" role="dialog" aria-modal="true">
        <div class="sxm-header">
          <span class="mdi mdi-shield-off-outline sxm-header-icon"></span>
          <div class="sxm-header-text">
            <span class="sxm-header-title">Exentar servicios</span>
            <span class="sxm-header-sub">{{ row.tenantName }} · {{ row.plotName }}</span>
          </div>
          <button class="sxm-close" :disabled="saving" @click="onClose"><span class="mdi mdi-close"></span></button>
        </div>

        <!-- Paso 1: qué se exenta -->
        <div v-if="step === 1" class="sxm-body">
          <div class="sxm-section-head">
            <span class="sxm-label">Servicios a exentar</span>
            <button type="button" class="sxm-link" @click="toggleAll">{{ allSelected ? 'Quitar todos' : 'Seleccionar todos' }}</button>
          </div>
          <label v-for="b in candidates" :key="b.serviceId" class="sxm-service">
            <input type="checkbox" :value="b.serviceId" v-model="selected" />
            <span class="mdi sxm-service-icon" :class="b.icon"></span>
            <span class="sxm-service-name">{{ b.serviceName }}</span>
            <span class="sxm-service-amount">{{ formatCurrency(b.grossAmount) }}</span>
          </label>
          <p v-if="!candidates.length" class="sxm-empty">Este inquilino no tiene servicios con monto en este mes.</p>

          <span class="sxm-label sxm-label--spaced">¿Cuánto se exenta?</span>
          <div class="sxm-modes">
            <button type="button" class="sxm-mode" :class="{ 'sxm-mode--active': mode === 'full' }" @click="mode = 'full'">Completo (100%)</button>
            <button type="button" class="sxm-mode" :class="{ 'sxm-mode--active': mode === 'partial' }" @click="mode = 'partial'">Parcial</button>
          </div>
          <div v-if="mode === 'partial'" class="sxm-percent">
            <input v-model.number="percent" type="number" min="1" max="99" step="1" class="sxm-input sxm-input--short" />
            <span>% de su parte en cada servicio elegido</span>
          </div>

          <label class="sxm-label sxm-label--spaced" for="sxm-reason">Motivo (opcional)</label>
          <input id="sxm-reason" v-model.trim="reason" type="text" maxlength="300" class="sxm-input" placeholder="Ej. Acuerdo por obra en su nave" />

          <div class="sxm-summary">
            <span>Monto que se exenta</span>
            <strong>{{ formatCurrency(exemptAmount) }}</strong>
          </div>
          <p v-if="error" class="sxm-error">{{ error }}</p>
        </div>

        <!-- Paso 2: qué pasa con el monto -->
        <div v-else class="sxm-body">
          <p class="sxm-question">¿Qué hacemos con los <strong>{{ formatCurrency(exemptAmount) }}</strong> exentados?</p>
          <p class="sxm-note">Este monto no se reparte entre los demás inquilinos.</p>

          <button type="button" class="sxm-choice" :class="{ 'sxm-choice--active': resolution === 'ABSORB' }" @click="resolution = 'ABSORB'">
            <span class="mdi mdi-bank-outline sxm-choice-icon"></span>
            <span class="sxm-choice-text">
              <strong>Absorberlo</strong>
              <span>Lo paga el parque. Se registra como gasto operativo y aparece en Finanzas (Servicios comunes), lo que baja el NOI.</span>
            </span>
          </button>
          <button type="button" class="sxm-choice" :class="{ 'sxm-choice--active': resolution === 'VOID' }" @click="resolution = 'VOID'">
            <span class="mdi mdi-cancel sxm-choice-icon"></span>
            <span class="sxm-choice-text">
              <strong>Anularlo</strong>
              <span>No se cobra a nadie y no se registra como gasto. Solo queda constancia de la exención en este mes.</span>
            </span>
          </button>
          <p v-if="error" class="sxm-error">{{ error }}</p>
        </div>

        <div class="sxm-footer">
          <button v-if="step === 2" class="sxm-btn sxm-btn--ghost" :disabled="saving" @click="step = 1">Atrás</button>
          <button v-else class="sxm-btn sxm-btn--ghost" @click="onClose">Cancelar</button>
          <button v-if="step === 1" class="sxm-btn sxm-btn--primary" :disabled="!canContinue" @click="goNext">Continuar</button>
          <button v-else class="sxm-btn sxm-btn--primary" :disabled="!resolution || saving" @click="save">
            {{ saving ? 'Guardando...' : 'Exentar' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { saveServiceExemptions } from '@/services/services'

const props = defineProps({
  periodId: { type: String, required: true },
  row: { type: Object, required: true },
})
const emit = defineEmits(['close', 'saved'])

const candidates = computed(() => props.row.breakdown.filter((b) => b.grossAmount > 0))
const selected = ref(candidates.value.map((b) => b.serviceId))
const mode = ref('full')
const percent = ref(50)
const reason = ref('')
const resolution = ref('')
const step = ref(1)
const saving = ref(false)
const error = ref('')

const allSelected = computed(() => selected.value.length === candidates.value.length && candidates.value.length > 0)
const effectivePercent = computed(() => (mode.value === 'full' ? 100 : Number(percent.value)))
const percentValid = computed(() => mode.value === 'full' || (Number.isFinite(effectivePercent.value) && effectivePercent.value >= 1 && effectivePercent.value <= 99))
const exemptAmount = computed(() => {
  const gross = candidates.value.filter((b) => selected.value.includes(b.serviceId)).reduce((s, b) => s + b.grossAmount, 0)
  return Math.round(gross * (percentValid.value ? effectivePercent.value : 0)) / 100
})
const canContinue = computed(() => selected.value.length > 0 && percentValid.value && exemptAmount.value > 0)

function toggleAll() {
  selected.value = allSelected.value ? [] : candidates.value.map((b) => b.serviceId)
}

function goNext() {
  error.value = ''
  step.value = 2
}

async function save() {
  saving.value = true
  error.value = ''
  try {
    await saveServiceExemptions(props.periodId, {
      contractId: props.row.contractId,
      serviceIds: selected.value,
      percent: effectivePercent.value,
      resolution: resolution.value,
      reason: reason.value || undefined,
    })
    emit('saved')
  } catch (err) {
    error.value = err.message
    step.value = 1
  } finally {
    saving.value = false
  }
}

function onClose() {
  if (!saving.value) emit('close')
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 }).format(n || 0)
}
</script>

<style scoped>
.sxm-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 1rem; }
.sxm-modal { width: 480px; max-width: 100%; max-height: 90vh; background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column; overflow: hidden; }
.sxm-header { flex-shrink: 0; background: #0f172a; display: flex; align-items: center; gap: 0.6rem; padding: 0.9rem 1.25rem; }
.sxm-header-icon { color: #fff; font-size: 22px; }
.sxm-header-text { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.sxm-header-title { font-family: var(--font-display); font-weight: 700; font-size: 16px; color: #fff; }
.sxm-header-sub { font-family: var(--font-body); font-size: 12px; color: rgba(255, 255, 255, 0.65); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sxm-close { background: none; border: none; color: rgba(255, 255, 255, 0.6); cursor: pointer; display: flex; }

.sxm-body { padding: 1.1rem 1.25rem; overflow-y: auto; font-family: var(--font-body); color: #334155; }
.sxm-section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.sxm-label { display: block; font-size: 12px; font-weight: 500; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.sxm-label--spaced { margin: 1rem 0 0.4rem; }
.sxm-link { background: none; border: none; color: #3b82f6; font-size: 12px; cursor: pointer; }
.sxm-service { display: flex; align-items: center; gap: 0.5rem; padding: 0.45rem 0.5rem; border-radius: 8px; cursor: pointer; font-size: 13px; }
.sxm-service:hover { background: #f1f5f9; }
.sxm-service-icon { color: #94a3b8; font-size: 16px; }
.sxm-service-name { flex: 1; min-width: 0; }
.sxm-service-amount { font-family: var(--font-mono); font-size: 12px; color: #1e3a5f; }
.sxm-empty { font-size: 13px; color: #94a3b8; }

.sxm-modes { display: flex; gap: 0.5rem; }
.sxm-mode { flex: 1; padding: 0.5rem; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8f9fc; font-size: 13px; color: #64748b; cursor: pointer; }
.sxm-mode--active { border-color: #3b82f6; background: #eff6ff; color: #1d4ed8; font-weight: 500; }
.sxm-percent { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.6rem; font-size: 13px; }
.sxm-input { width: 100%; font-family: var(--font-body); font-size: 13px; color: #1e3a5f; border: 1px solid var(--border-active); border-radius: 8px; padding: 0.5rem 0.65rem; box-sizing: border-box; }
.sxm-input--short { width: 80px; }
.sxm-input:focus { outline: none; border-color: #3b82f6; }

.sxm-summary { display: flex; justify-content: space-between; align-items: center; margin-top: 1.1rem; padding: 0.65rem 0.8rem; background: #f8f9fc; border-radius: 8px; font-size: 13px; }
.sxm-summary strong { font-family: var(--font-mono); font-size: 15px; color: #1e3a5f; }

.sxm-question { margin: 0 0 0.25rem; font-size: 15px; color: #1e3a5f; }
.sxm-note { margin: 0 0 1rem; font-size: 12px; color: #94a3b8; }
.sxm-choice { display: flex; gap: 0.7rem; align-items: flex-start; width: 100%; text-align: left; margin-bottom: 0.6rem; padding: 0.8rem; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; cursor: pointer; font-family: var(--font-body); }
.sxm-choice--active { border-color: #3b82f6; background: #eff6ff; }
.sxm-choice-icon { font-size: 22px; color: #3b82f6; margin-top: 2px; }
.sxm-choice-text { display: flex; flex-direction: column; gap: 0.2rem; font-size: 12px; color: #64748b; line-height: 1.45; }
.sxm-choice-text strong { font-size: 14px; color: #1e3a5f; }
.sxm-error { margin: 0.8rem 0 0; font-size: 12px; color: #dc2626; line-height: 1.4; }

.sxm-footer { flex-shrink: 0; display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.25rem; border-top: 1px solid #f1f5f9; background: #f8f9fc; }
.sxm-btn { height: 36px; padding: 0 1rem; border-radius: 8px; border: 1px solid transparent; font-family: var(--font-body); font-size: 13px; cursor: pointer; }
.sxm-btn--ghost { background: #fff; border-color: #e2e8f0; color: #64748b; }
.sxm-btn--primary { background: #3b82f6; color: #fff; font-weight: 500; }
.sxm-btn:disabled { opacity: 0.5; cursor: default; }
</style>